/**
 * GrowthBook Feature Configuration
 * Maps features to subscription tiers and provides type-safe feature keys
 */

export type PlanType = 'free' | 'plus' | 'pro' | 'enterprise';

export type FeatureKey =
  | 'visibility_public'
  | 'visibility_domain_only'
  | 'visibility_always_private'
  | 'team_members'
  | 'ai_enhancements'
  | 'custom_branding'
  | 'api_access'
  | 'unlimited_posts'
  | 'advanced_analytics'
  | 'byline_authors'
  | 'video_4k_export'
  | 'bulk_operations'
  | 'priority_support'
  | 'transcript_editing'
  | 'moments_clear_view_limit'
  | 'moments_blur_locked'
  | 'moments_export_enabled'
  | 'moments_preview_on_hover'
  | 'moments_show_total_count'
  | 'moments_export_quality'
  // Upload source features (environment-based, not plan-based)
  | 'video_upload_cloud_sources'
  | 'video_upload_zoom';

// Feature availability by plan
export const FEATURE_MATRIX: Record<FeatureKey, PlanType[]> = {
  // Visibility Features
  'visibility_public': ['free', 'plus', 'pro', 'enterprise'], // Everyone gets public
  'visibility_domain_only': ['pro', 'enterprise'], // Pro only
  'visibility_always_private': ['pro', 'enterprise'], // Pro only
  
  // Team Features  
  'team_members': ['plus', 'pro', 'enterprise'],
  'byline_authors': ['pro', 'enterprise'],
  
  // Content Features
  'unlimited_posts': ['plus', 'pro', 'enterprise'],
  'video_4k_export': ['pro', 'enterprise'],
  'bulk_operations': ['pro', 'enterprise'],
  
  // AI Features
  'ai_enhancements': ['plus', 'pro', 'enterprise'],
  
  // Advanced Features
  'custom_branding': ['pro', 'enterprise'],
  'api_access': ['pro', 'enterprise'],
  'advanced_analytics': ['pro', 'enterprise'],
  'priority_support': ['pro', 'enterprise'],
  
  // Transcript Features
  'transcript_editing': ['plus', 'pro', 'enterprise'], // Not available for free tier
  
  // Moment Features
  'moments_clear_view_limit': ['free', 'plus', 'pro', 'enterprise'], // All plans have different limits
  'moments_blur_locked': ['free', 'plus'], // Free and Plus have blurred moments
  'moments_export_enabled': ['plus', 'pro', 'enterprise'], // Export only for paid plans
  'moments_preview_on_hover': ['plus', 'pro', 'enterprise'], // Preview on hover for paid plans
  'moments_show_total_count': ['free', 'plus', 'pro', 'enterprise'], // Everyone sees total count
  'moments_export_quality': ['plus', 'pro', 'enterprise'], // Export quality varies by plan

  // Upload Source Features (environment-controlled via GrowthBook targeting, not plan-based)
  // These are set to all plans but controlled by environment targeting in GrowthBook
  'video_upload_cloud_sources': [], // Empty = controlled by GrowthBook environment targeting
  'video_upload_zoom': [], // Empty = controlled by GrowthBook environment targeting
};

// Helper function to check if a plan has access to a feature
export function planHasFeature(plan: PlanType, feature: FeatureKey): boolean {
  return FEATURE_MATRIX[feature]?.includes(plan) || false;
}

// Get the minimum required plan for a feature
export function getRequiredPlan(feature: FeatureKey): PlanType | null {
  const plans = FEATURE_MATRIX[feature];
  if (!plans || plans.length === 0) return null;
  
  // Return the lowest tier that has access
  if (plans.includes('free')) return 'free';
  if (plans.includes('plus')) return 'plus';
  if (plans.includes('pro')) return 'pro';
  if (plans.includes('enterprise')) return 'enterprise';
  
  return null;
}

// Plan display names
export const PLAN_DISPLAY_NAMES: Record<PlanType, string> = {
  free: 'Starter',
  plus: 'Plus',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

// Plan upgrade paths
export const UPGRADE_PATHS: Record<PlanType, PlanType[]> = {
  free: ['plus', 'pro', 'enterprise'],
  plus: ['pro', 'enterprise'],
  pro: ['enterprise'],
  enterprise: [],
};

// Feature display information
export const FEATURE_INFO: Record<FeatureKey, { name: string; description: string }> = {
  'visibility_public': {
    name: 'Public Posts',
    description: 'Share your content with the world',
  },
  'visibility_domain_only': {
    name: 'Domain-Only Posts',
    description: 'Keep content private to your domain members',
  },
  'visibility_always_private': {
    name: 'Always Private Posts',
    description: 'Posts that remain private even when published',
  },
  'team_members': {
    name: 'Team Collaboration',
    description: 'Add team members to your domain',
  },
  'ai_enhancements': {
    name: 'AI Enhancements',
    description: 'AI-powered video and audio improvements',
  },
  'custom_branding': {
    name: 'Custom Branding',
    description: 'White-label your domain',
  },
  'api_access': {
    name: 'API Access',
    description: 'Programmatic access to your content',
  },
  'unlimited_posts': {
    name: 'Unlimited Posts',
    description: 'No monthly post limits',
  },
  'advanced_analytics': {
    name: 'Advanced Analytics',
    description: 'Detailed insights and metrics',
  },
  'byline_authors': {
    name: 'Byline Authors',
    description: 'Guest contributors without seats',
  },
  'video_4k_export': {
    name: '4K Video Export',
    description: 'Export videos in 4K resolution',
  },
  'bulk_operations': {
    name: 'Bulk Operations',
    description: 'Process multiple items at once',
  },
  'priority_support': {
    name: 'Priority Support',
    description: '24/7 support with faster response times',
  },
  'transcript_editing': {
    name: 'Transcript Editing',
    description: 'Edit and correct video transcripts',
  },
  'moments_clear_view_limit': {
    name: 'Clear Moment Viewing',
    description: 'Number of moments viewable without blur',
  },
  'moments_blur_locked': {
    name: 'Blurred Moments',
    description: 'Some moments are blurred based on your plan',
  },
  'moments_export_enabled': {
    name: 'Moment Export',
    description: 'Export individual moments as video clips',
  },
  'moments_preview_on_hover': {
    name: 'Moment Preview',
    description: 'Preview moments on hover',
  },
  'moments_show_total_count': {
    name: 'Total Moments Display',
    description: 'See total number of AI-detected moments',
  },
  'moments_export_quality': {
    name: 'Export Quality',
    description: 'Video export quality based on your plan',
  },
  'video_upload_cloud_sources': {
    name: 'Cloud Storage Import',
    description: 'Import videos from Google Drive and OneDrive',
  },
  'video_upload_zoom': {
    name: 'Zoom Recording Import',
    description: 'Import recordings from Zoom cloud',
  },
};

// Feature value configurations
export const FEATURE_VALUES = {
  moments_clear_view_limit: {
    free: 5,    // Starter users see 5 moments clearly
    plus: 10,   // Plus users see 10 moments clearly
    pro: -1,    // Pro users see all moments clearly (-1 = unlimited)
    enterprise: -1
  },
  moments_export_quality: {
    free: '480p',
    plus: '1080p',
    pro: '4k',
    enterprise: '4k'
  },
  moments_preview_duration: {
    free: 0,     // No preview for free users
    plus: 3,     // 3 second preview for plus
    pro: -1,     // Full preview for pro (-1 = unlimited)
    enterprise: -1
  }
};

// Helper function to get feature value by plan
export function getFeatureValue(feature: string, plan: PlanType): any {
  return FEATURE_VALUES[feature as keyof typeof FEATURE_VALUES]?.[plan];
}

// GrowthBook attribute types
export interface GrowthBookAttributes {
  userId: string;
  domainId: string;
  planType: PlanType;
  seats: number;
  seatsUsed: number;
  credits: number;
  isOwner: boolean;
  userRole: 'owner' | 'admin' | 'contributor' | 'byline' | 'viewer';
}